import type { AgentRunStatus } from "./types";

export const allowedAgentRunTransitions: Record<AgentRunStatus, readonly AgentRunStatus[]> = {
  queued: ["running", "canceled"],
  running: ["waiting_for_human", "failed", "completed", "canceled"],
  waiting_for_human: ["running", "failed", "completed", "canceled"],
  failed: [],
  completed: [],
  canceled: [],
};

export type AgentRunMutableActionId = "mark_running" | "request_human" | "complete" | "fail";

export type AgentRunMutableAction = {
  id: AgentRunMutableActionId;
  label: string;
  targetStatus: AgentRunStatus;
  requiredField?: "summary" | "error";
};

const agentRunMutableActions: Record<AgentRunMutableActionId, AgentRunMutableAction> = {
  mark_running: { id: "mark_running", label: "Mark running", targetStatus: "running" },
  request_human: {
    id: "request_human",
    label: "Request human",
    targetStatus: "waiting_for_human",
    requiredField: "summary",
  },
  complete: {
    id: "complete",
    label: "Complete",
    targetStatus: "completed",
    requiredField: "summary",
  },
  fail: { id: "fail", label: "Fail", targetStatus: "failed", requiredField: "error" },
};

const mutableActionIdsByStatus: Record<AgentRunStatus, readonly AgentRunMutableActionId[]> = {
  queued: ["mark_running"],
  running: ["request_human", "complete", "fail"],
  waiting_for_human: ["mark_running", "complete", "fail"],
  failed: [],
  completed: [],
  canceled: [],
};

export type AgentRunTransitionInput = {
  from: AgentRunStatus;
  to: AgentRunStatus;
  summary?: string;
  error?: string;
};

export function canTransitionAgentRunStatus(from: AgentRunStatus, to: AgentRunStatus): boolean {
  if (from === to) {
    return true;
  }

  return allowedAgentRunTransitions[from].includes(to);
}

export function getAgentRunMutableActions(status: AgentRunStatus): AgentRunMutableAction[] {
  return mutableActionIdsByStatus[status].map((id) => agentRunMutableActions[id]);
}

export function validateAgentRunTransition(input: AgentRunTransitionInput): {
  ok: boolean;
  reason?: string;
} {
  if (!canTransitionAgentRunStatus(input.from, input.to)) {
    return { ok: false, reason: `Cannot transition agent run from ${input.from} to ${input.to}` };
  }

  if (input.to === "completed" && !hasText(input.summary)) {
    return { ok: false, reason: "Completed agent runs require a summary" };
  }

  if (input.to === "failed" && !hasText(input.error)) {
    return { ok: false, reason: "Failed agent runs require an error" };
  }

  return { ok: true };
}

function hasText(value: string | undefined): boolean {
  return typeof value === "string" && value.trim().length > 0;
}
